import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import BulkUploadModal from '../components/BulkUploadModal';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Upload, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EmployeeStatutoryEligibility = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [employees, setEmployees] = useState([]);
  const [statInfos, setStatInfos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busyToggle, setBusyToggle] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    employee_id: '',
    pf: { uan: '', pf_number: '', is_eligible: true, voluntary_higher: false, voluntary_rate: 12 },
    esi: { esic_number: '', is_eligible: true, dispensary: '', ip_number: '' },
    pt_eligible: true, pt_state: '', lwf_eligible: true, lwf_state: '',
    tds: { pan: '', regime: 'new' },
    payment_responsibility: 'COMPANY_PAYS',
    client_company_id: '',
    gratuity_eligible: false,
    bonus_eligible: true,
    leave_encashment_eligible: false,
    gratuity: { is_eligible: false, frequency: 'monthly', amount_mode: 'auto_calculate', eligibility_period: '', calculation_period: '', period_lock: false, payment_responsibility: 'COMPANY_PAYS', client_company_id: '' },
    bonus: { is_eligible: true, frequency: 'annual', amount_mode: 'auto_calculate', eligibility_period: '', calculation_period: '', period_lock: false, payment_responsibility: 'COMPANY_PAYS', client_company_id: '' },
    leave_encashment: { is_eligible: false, frequency: 'annual', amount_mode: 'auto_calculate', eligibility_period: '', calculation_period: '', period_lock: false, payment_responsibility: 'COMPANY_PAYS', client_company_id: '' },
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data); setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);
  const fetchStatInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/statutory/employee-info?company_id=${selectedCompany}`, { withCredentials: true });
      setStatInfos(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) { fetchEmployees(); fetchStatInfo(); } }, [selectedCompany, fetchEmployees, fetchStatInfo]);

  const getEmpName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (!emp) return empId;
    return `${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}`.trim();
  };

  const getEmpCode = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.employment_details?.employee_code || '';
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        gratuity_eligible: form.gratuity?.is_eligible ?? form.gratuity_eligible,
        bonus_eligible: form.bonus?.is_eligible ?? form.bonus_eligible,
        leave_encashment_eligible: form.leave_encashment?.is_eligible ?? form.leave_encashment_eligible,
      };
      if (editEmp) {
        await axios.patch(`${API_URL}/statutory/employee-info/${form.employee_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/statutory/employee-info`, { ...payload, company_id: selectedCompany }, { withCredentials: true });
      }
      setShowModal(false); setEditEmp(null); fetchStatInfo();
      toast.success('Statutory configuration saved successfully');
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const openEdit = (info) => {
    setEditEmp(info);
    const mainResp = info.payment_responsibility || 'COMPANY_PAYS';
    const mainClient = info.client_company_id || '';

    const grat = info.gratuity || {};
    const bon = info.bonus || {};
    const le = info.leave_encashment || {};

    const gratElig = grat.is_eligible ?? info.gratuity_eligible ?? false;
    const bonElig = bon.is_eligible ?? info.bonus_eligible ?? true;
    const leElig = le.is_eligible ?? info.leave_encashment_eligible ?? false;

    setForm({
      employee_id: info.employee_id,
      pf: info.pf || { uan: '', pf_number: '', is_eligible: true, voluntary_higher: false, voluntary_rate: 12 },
      esi: info.esi || { esic_number: '', is_eligible: true, dispensary: '', ip_number: '' },
      pt_eligible: info.pt_eligible !== false, pt_state: info.pt_state || '', lwf_eligible: info.lwf_eligible !== false, lwf_state: info.lwf_state || '',
      tds: info.tds || { pan: '', regime: 'new' },
      payment_responsibility: mainResp,
      client_company_id: mainClient,
      gratuity_eligible: gratElig,
      bonus_eligible: bonElig,
      leave_encashment_eligible: leElig,
      gratuity: {
        is_eligible: gratElig,
        frequency: grat.frequency || 'monthly',
        amount_mode: grat.amount_mode || 'auto_calculate',
        eligibility_period: grat.eligibility_period || '',
        calculation_period: grat.calculation_period || '',
        period_lock: grat.period_lock || false,
        payment_responsibility: grat.payment_responsibility || mainResp,
        client_company_id: grat.client_company_id || mainClient,
      },
      bonus: {
        is_eligible: bonElig,
        frequency: bon.frequency || 'annual',
        amount_mode: bon.amount_mode || 'auto_calculate',
        eligibility_period: bon.eligibility_period || '',
        calculation_period: bon.calculation_period || '',
        period_lock: bon.period_lock || false,
        payment_responsibility: bon.payment_responsibility || mainResp,
        client_company_id: bon.client_company_id || mainClient,
      },
      leave_encashment: {
        is_eligible: leElig,
        frequency: le.frequency || 'annual',
        amount_mode: le.amount_mode || 'auto_calculate',
        eligibility_period: le.eligibility_period || '',
        calculation_period: le.calculation_period || '',
        period_lock: le.period_lock || false,
        payment_responsibility: le.payment_responsibility || mainResp,
        client_company_id: le.client_company_id || mainClient,
      },
    });
    setShowModal(true);
  };

  const filtered = statInfos.filter((s) => {
    if (!search) return true;
    const name = getEmpName(s.employee_id).toLowerCase();
    const code = getEmpCode(s.employee_id).toLowerCase();
    return name.includes(search.toLowerCase()) || code.includes(search.toLowerCase());
  });

  const enrolledSet = new Set(statInfos.map(s => s.employee_id));

  // Bulk-toggle is the 1-click "make these N employees PF/ESI/PT eligible (or not)" feature.
  // Works on whatever rows are checked — operates on employee_ids (not stat-info ids), so
  // employees who don't yet have a statutory_info row will get a baseline row created.
  const bulkToggle = async (field, value) => {
    if (selectedIds.size === 0) return;
    setBusyToggle(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/statutory/employee-info/bulk-toggle?company_id=${selectedCompany}`,
        { employee_ids: Array.from(selectedIds), field, value },
        { withCredentials: true },
      );
      const total = (data.updated_count || 0) + (data.created_count || 0);
      toast.success(`${total} employee${total === 1 ? '' : 's'} updated · ${field} = ${value ? 'eligible' : 'not eligible'}`);
      setSelectedIds(new Set());
      fetchStatInfo();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk toggle failed');
    } finally {
      setBusyToggle(false);
    }
  };

  const toggleSelect = (empId) => {
    const next = new Set(selectedIds);
    if (next.has(empId)) next.delete(empId); else next.add(empId);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(s => s.employee_id)));
    }
  };

  // Reset selection when company changes — IDs are scoped to the active company.
  useEffect(() => { setSelectedIds(new Set()); }, [selectedCompany]);

  const TOGGLE_FIELDS = [
    { key: 'pf.is_eligible', label: 'PF' },
    { key: 'esi.is_eligible', label: 'ESI' },
    { key: 'pt_eligible', label: 'PT' },
    { key: 'lwf_eligible', label: 'LWF' },
    { key: 'gratuity_eligible', label: 'Gratuity' },
    { key: 'bonus_eligible', label: 'Bonus' },
  ];

  return (
    <MainLayout>
      <div data-testid="employee-statutory-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Employee Statutory Eligibility</h1>
            <p className="text-sm text-slate-500 mt-1">Manage PF/ESI/PT/TDS eligibility and details per employee</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="bulk-upload-stat-btn" onClick={() => setShowBulkUpload(true)}
              className="flex items-center space-x-2 bg-white border border-[#0052FF] text-[#0052FF] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0052FF]/5">
              <Upload className="w-4 h-4" /><span>Bulk Upload</span>
            </button>
            <button data-testid="add-stat-info-btn" onClick={() => { setEditEmp(null); setForm({ employee_id: '', pf: { uan: '', pf_number: '', is_eligible: true, voluntary_higher: false, voluntary_rate: 12 }, esi: { esic_number: '', is_eligible: true, dispensary: '', ip_number: '' }, pt_eligible: true, pt_state: '', lwf_eligible: true, tds: { pan: '', regime: 'new' }, gratuity_eligible: false, bonus_eligible: true }); setShowModal(true); }}
              className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC]">
              <Plus className="w-4 h-4" /><span>Enroll Employee</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{statInfos.filter(s => s.pf?.is_eligible).length}</p>
            <p className="text-xs text-blue-500">PF Eligible</p>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 text-center">
            <p className="text-2xl font-bold text-emerald-700">{statInfos.filter(s => s.esi?.is_eligible).length}</p>
            <p className="text-xs text-emerald-500">ESI Eligible</p>
          </div>
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{statInfos.filter(s => s.pt_eligible).length}</p>
            <p className="text-xs text-amber-500">PT Eligible</p>
          </div>
          <div className="bg-red-50 rounded-xl border border-red-200 p-4 text-center">
            <p className="text-2xl font-bold text-red-700">{statInfos.length}</p>
            <p className="text-xs text-red-500">Total Enrolled</p>
          </div>
        </div>

        <div className="relative"><Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or code..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm" /></div>

        {/* Bulk-edit toolbar — appears only when ≥1 row is selected. */}
        {selectedIds.size > 0 && (
          <div data-testid="bulk-edit-toolbar" className="bg-[#0052FF]/5 border border-[#0052FF]/30 rounded-xl px-4 py-3 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-700 mr-2" data-testid="bulk-edit-selected-count">
              {selectedIds.size} selected
            </span>
            {TOGGLE_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center gap-1">
                <button
                  data-testid={`bulk-enable-${f.label.toLowerCase()}`}
                  onClick={() => bulkToggle(f.key, true)}
                  disabled={busyToggle}
                  title={`Mark all selected employees as ${f.label} eligible`}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md hover:bg-emerald-100 disabled:opacity-50">
                  <ToggleRight className="w-3.5 h-3.5" /> {f.label} ON
                </button>
                <button
                  data-testid={`bulk-disable-${f.label.toLowerCase()}`}
                  onClick={() => bulkToggle(f.key, false)}
                  disabled={busyToggle}
                  title={`Mark all selected employees as ${f.label} NOT eligible`}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50">
                  <ToggleLeft className="w-3.5 h-3.5" /> {f.label} OFF
                </button>
              </div>
            ))}
            <button
              data-testid="bulk-clear-selection"
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <th className="px-3 py-3 text-center font-medium w-10">
                <input
                  data-testid="bulk-select-all"
                  type="checkbox"
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onChange={toggleSelectAll}
                  className="rounded border-slate-300"
                />
              </th>
              <th className="px-4 py-3 text-left font-medium">Employee</th>
              <th className="px-4 py-3 text-left font-medium">UAN/PF</th>
              <th className="px-4 py-3 text-left font-medium">ESIC</th>
              <th className="px-4 py-3 text-left font-medium">PAN</th>
              <th className="px-4 py-3 text-center font-medium">PF</th>
              <th className="px-4 py-3 text-center font-medium">ESI</th>
              <th className="px-4 py-3 text-center font-medium">PT</th>
              <th className="px-4 py-3 text-center font-medium">Regime</th>
              <th className="px-4 py-3 text-center font-medium">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`stat-row-${s.employee_id}`} className={`hover:bg-slate-50 ${selectedIds.has(s.employee_id) ? 'bg-blue-50/30' : ''}`}>
                  <td className="px-3 py-3 text-center">
                    <input
                      data-testid={`bulk-select-${s.employee_id}`}
                      type="checkbox"
                      checked={selectedIds.has(s.employee_id)}
                      onChange={() => toggleSelect(s.employee_id)}
                      className="rounded border-slate-300"
                    />
                  </td>
                  <td className="px-4 py-3"><p className="font-medium text-slate-800">{getEmpName(s.employee_id)}</p><p className="text-xs text-slate-500">{getEmpCode(s.employee_id)}</p></td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-600">{s.pf?.uan || '-'}</td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-600">{s.esi?.esic_number || '-'}</td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-600">{s.tds?.pan || '-'}</td>
                  <td className="px-4 py-3 text-center"><span className={`inline-block w-2 h-2 rounded-full ${s.pf?.is_eligible ? 'bg-emerald-400' : 'bg-slate-300'}`} /></td>
                  <td className="px-4 py-3 text-center"><span className={`inline-block w-2 h-2 rounded-full ${s.esi?.is_eligible ? 'bg-emerald-400' : 'bg-slate-300'}`} /></td>
                  <td className="px-4 py-3 text-center"><span className={`inline-block w-2 h-2 rounded-full ${s.pt_eligible ? 'bg-emerald-400' : 'bg-slate-300'}`} /></td>
                  <td className="px-4 py-3 text-center"><span className="px-2 py-0.5 rounded text-xs bg-slate-100">{s.tds?.regime?.toUpperCase() || 'NEW'}</span></td>
                  <td className="px-4 py-3 text-center"><button data-testid={`edit-stat-${s.employee_id}`} onClick={() => openEdit(s)} className="text-[#0052FF] hover:underline text-xs font-medium">Edit</button></td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">No statutory info found</td></tr>}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 overflow-y-auto" onClick={() => setShowModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-xl shadow-xl my-8" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">{editEmp ? 'Edit Statutory Info' : 'Enroll Employee'}</h3>
              <form onSubmit={handleSave} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                {!editEmp && (
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Employee</label>
                    <select data-testid="stat-employee-select" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required>
                      <option value="">Select Employee</option>
                      {employees.filter(e => !enrolledSet.has(e.id)).map((e) => <option key={e.id} value={e.id}>{`${e.personal_details?.first_name} ${e.personal_details?.last_name} (${e.employment_details?.employee_code})`}</option>)}
                    </select></div>
                )}
                <div className="border border-slate-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-700 mb-2">Provident Fund</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs text-slate-600 mb-1">UAN</label><input value={form.pf.uan} onChange={(e) => setForm({ ...form, pf: { ...form.pf, uan: e.target.value } })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                    <div><label className="block text-xs text-slate-600 mb-1">PF Number</label><input value={form.pf.pf_number} onChange={(e) => setForm({ ...form, pf: { ...form.pf, pf_number: e.target.value } })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                  </div>
                  <div className="flex items-center space-x-4 mt-2">
                    <label className="flex items-center space-x-1"><input type="checkbox" checked={form.pf.is_eligible} onChange={(e) => setForm({ ...form, pf: { ...form.pf, is_eligible: e.target.checked } })} /><span className="text-xs">PF Eligible</span></label>
                    <label className="flex items-center space-x-1"><input type="checkbox" checked={form.pf.voluntary_higher} onChange={(e) => setForm({ ...form, pf: { ...form.pf, voluntary_higher: e.target.checked } })} /><span className="text-xs">Voluntary Higher PF</span></label>
                    {form.pf.voluntary_higher && <input type="number" step="0.1" value={form.pf.voluntary_rate} onChange={(e) => setForm({ ...form, pf: { ...form.pf, voluntary_rate: parseFloat(e.target.value) } })} className="w-20 border border-slate-300 rounded px-2 py-1 text-xs" />}
                  </div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-emerald-700 mb-2">ESI</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs text-slate-600 mb-1">ESIC Number</label><input value={form.esi.esic_number} onChange={(e) => setForm({ ...form, esi: { ...form.esi, esic_number: e.target.value } })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                    <div><label className="block text-xs text-slate-600 mb-1">IP Number</label><input value={form.esi.ip_number} onChange={(e) => setForm({ ...form, esi: { ...form.esi, ip_number: e.target.value } })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                  </div>
                  <label className="flex items-center space-x-1 mt-2"><input type="checkbox" checked={form.esi.is_eligible} onChange={(e) => setForm({ ...form, esi: { ...form.esi, is_eligible: e.target.checked } })} /><span className="text-xs">ESI Eligible</span></label>
                </div>
                <div className="border border-slate-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-red-700 mb-2">TDS / Income Tax</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs text-slate-600 mb-1">PAN</label><input value={form.tds.pan} onChange={(e) => setForm({ ...form, tds: { ...form.tds, pan: e.target.value.toUpperCase() } })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" /></div>
                    <div><label className="block text-xs text-slate-600 mb-1">Tax Regime</label>
                      <select value={form.tds.regime} onChange={(e) => setForm({ ...form, tds: { ...form.tds, regime: e.target.value } })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
                        <option value="new">New Regime</option><option value="old">Old Regime</option>
                      </select></div>
                  </div>
                </div>
                {/* GRATUITY CONFIGURATION */}
                <div className="border border-indigo-200 rounded-lg p-3 bg-indigo-50/20">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-indigo-900">Gratuity Configuration</h4>
                    <label className="flex items-center space-x-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="gratuity-eligible-toggle"
                        checked={form.gratuity?.is_eligible ?? false}
                        onChange={(e) => setForm({
                          ...form,
                          gratuity_eligible: e.target.checked,
                          gratuity: { ...form.gratuity, is_eligible: e.target.checked }
                        })}
                        className="rounded text-indigo-600"
                      />
                      <span className="text-xs font-semibold text-indigo-900">Gratuity Eligible</span>
                    </label>
                  </div>

                  {form.gratuity?.is_eligible ? (
                    <div className="grid grid-cols-2 gap-2.5 mt-2">
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Frequency</label>
                        <select
                          value={form.gratuity?.frequency || 'monthly'}
                          onChange={(e) => setForm({ ...form, gratuity: { ...form.gratuity, frequency: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="annual">Annual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Amount Mode</label>
                        <select
                          value={form.gratuity?.amount_mode || 'auto_calculate'}
                          onChange={(e) => setForm({ ...form, gratuity: { ...form.gratuity, amount_mode: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="auto_calculate">Auto Calculate</option>
                          <option value="ctc_upload">CTC Upload</option>
                          <option value="manual">Manual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Eligibility Period</label>
                        <input
                          type="text"
                          placeholder="e.g. 5 Years of Service"
                          value={form.gratuity?.eligibility_period || ''}
                          onChange={(e) => setForm({ ...form, gratuity: { ...form.gratuity, eligibility_period: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Calculation Period</label>
                        <input
                          type="text"
                          placeholder="e.g. Monthly Accrual"
                          value={form.gratuity?.calculation_period || ''}
                          onChange={(e) => setForm({ ...form, gratuity: { ...form.gratuity, calculation_period: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Period Lock</label>
                        <select
                          value={form.gratuity?.period_lock ? 'YES' : 'NO'}
                          onChange={(e) => setForm({ ...form, gratuity: { ...form.gratuity, period_lock: e.target.value === 'YES' } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="NO">No</option>
                          <option value="YES">Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Payment Responsibility</label>
                        <select
                          value={form.gratuity?.payment_responsibility || 'COMPANY_PAYS'}
                          onChange={(e) => setForm({ ...form, gratuity: { ...form.gratuity, payment_responsibility: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white font-medium"
                        >
                          <option value="COMPANY_PAYS">Company Pays</option>
                          <option value="CLIENT_COMPANY_PAYS">Client Company Pays</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic">Gratuity disabled for this employee.</p>
                  )}
                </div>

                {/* BONUS CONFIGURATION */}
                <div className="border border-blue-200 rounded-lg p-3 bg-blue-50/20">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-blue-900">Bonus Configuration</h4>
                    <label className="flex items-center space-x-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="bonus-eligible-toggle"
                        checked={form.bonus?.is_eligible ?? true}
                        onChange={(e) => setForm({
                          ...form,
                          bonus_eligible: e.target.checked,
                          bonus: { ...form.bonus, is_eligible: e.target.checked }
                        })}
                        className="rounded text-blue-600"
                      />
                      <span className="text-xs font-semibold text-blue-900">Bonus Eligible</span>
                    </label>
                  </div>

                  {form.bonus?.is_eligible ? (
                    <div className="grid grid-cols-2 gap-2.5 mt-2">
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Frequency</label>
                        <select
                          value={form.bonus?.frequency || 'annual'}
                          onChange={(e) => setForm({ ...form, bonus: { ...form.bonus, frequency: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="annual">Annual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Amount Mode</label>
                        <select
                          value={form.bonus?.amount_mode || 'auto_calculate'}
                          onChange={(e) => setForm({ ...form, bonus: { ...form.bonus, amount_mode: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="auto_calculate">Auto Calculate</option>
                          <option value="ctc_upload">CTC Upload</option>
                          <option value="manual">Manual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Eligibility Period</label>
                        <input
                          type="text"
                          placeholder="e.g. FY 2026-27"
                          value={form.bonus?.eligibility_period || ''}
                          onChange={(e) => setForm({ ...form, bonus: { ...form.bonus, eligibility_period: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Calculation Period</label>
                        <input
                          type="text"
                          placeholder="e.g. 2026-04 to 2027-03"
                          value={form.bonus?.calculation_period || ''}
                          onChange={(e) => setForm({ ...form, bonus: { ...form.bonus, calculation_period: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Period Lock</label>
                        <select
                          value={form.bonus?.period_lock ? 'YES' : 'NO'}
                          onChange={(e) => setForm({ ...form, bonus: { ...form.bonus, period_lock: e.target.value === 'YES' } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="NO">No</option>
                          <option value="YES">Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Payment Responsibility</label>
                        <select
                          value={form.bonus?.payment_responsibility || 'COMPANY_PAYS'}
                          onChange={(e) => setForm({ ...form, bonus: { ...form.bonus, payment_responsibility: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white font-medium"
                        >
                          <option value="COMPANY_PAYS">Company Pays</option>
                          <option value="CLIENT_COMPANY_PAYS">Client Company Pays</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic">Bonus disabled for this employee.</p>
                  )}
                </div>

                {/* LEAVE ENCASHMENT CONFIGURATION */}
                <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/20">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-emerald-900">Leave Encashment Configuration</h4>
                    <label className="flex items-center space-x-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="leave-encashment-eligible-toggle"
                        checked={form.leave_encashment?.is_eligible ?? false}
                        onChange={(e) => setForm({
                          ...form,
                          leave_encashment_eligible: e.target.checked,
                          leave_encashment: { ...form.leave_encashment, is_eligible: e.target.checked }
                        })}
                        className="rounded text-emerald-600"
                      />
                      <span className="text-xs font-semibold text-emerald-900">Leave Encashment Eligible</span>
                    </label>
                  </div>

                  {form.leave_encashment?.is_eligible ? (
                    <div className="grid grid-cols-2 gap-2.5 mt-2">
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Frequency</label>
                        <select
                          value={form.leave_encashment?.frequency || 'annual'}
                          onChange={(e) => setForm({ ...form, leave_encashment: { ...form.leave_encashment, frequency: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="annual">Annual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Amount Mode</label>
                        <select
                          value={form.leave_encashment?.amount_mode || 'auto_calculate'}
                          onChange={(e) => setForm({ ...form, leave_encashment: { ...form.leave_encashment, amount_mode: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="auto_calculate">Auto Calculate</option>
                          <option value="ctc_upload">CTC Upload</option>
                          <option value="manual">Manual</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Eligibility Period</label>
                        <input
                          type="text"
                          placeholder="e.g. FY 2026-27"
                          value={form.leave_encashment?.eligibility_period || ''}
                          onChange={(e) => setForm({ ...form, leave_encashment: { ...form.leave_encashment, eligibility_period: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Calculation Period</label>
                        <input
                          type="text"
                          placeholder="e.g. 2026-04 to 2027-03"
                          value={form.leave_encashment?.calculation_period || ''}
                          onChange={(e) => setForm({ ...form, leave_encashment: { ...form.leave_encashment, calculation_period: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Period Lock</label>
                        <select
                          value={form.leave_encashment?.period_lock ? 'YES' : 'NO'}
                          onChange={(e) => setForm({ ...form, leave_encashment: { ...form.leave_encashment, period_lock: e.target.value === 'YES' } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="NO">No</option>
                          <option value="YES">Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-0.5">Payment Responsibility</label>
                        <select
                          value={form.leave_encashment?.payment_responsibility || 'COMPANY_PAYS'}
                          onChange={(e) => setForm({ ...form, leave_encashment: { ...form.leave_encashment, payment_responsibility: e.target.value } })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white font-medium"
                        >
                          <option value="COMPANY_PAYS">Company Pays</option>
                          <option value="CLIENT_COMPANY_PAYS">Client Company Pays</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic">Leave Encashment disabled for this employee.</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-slate-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-amber-700 mb-2">Professional Tax</h4>
                    <label className="flex items-center space-x-1"><input type="checkbox" checked={form.pt_eligible} onChange={(e) => setForm({ ...form, pt_eligible: e.target.checked })} /><span className="text-xs">PT Eligible</span></label>
                    <input value={form.pt_state} onChange={(e) => setForm({ ...form, pt_state: e.target.value })} placeholder="State" className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-2" />
                  </div>
                  <div className="border border-slate-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Labour Welfare Fund</h4>
                    <label className="flex items-center space-x-1"><input type="checkbox" checked={form.lwf_eligible} onChange={(e) => setForm({ ...form, lwf_eligible: e.target.checked })} data-testid="lwf-eligible-checkbox" /><span className="text-xs">LWF Eligible</span></label>
                    <input value={form.lwf_state} onChange={(e) => setForm({ ...form, lwf_state: e.target.value })} placeholder="LWF State (optional)" className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs mt-1" data-testid="lwf-state-input" />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="save-stat-info-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <BulkUploadModal
          open={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          title="Bulk Upload Statutory Eligibility"
          description="Upload a CSV to set PF / ESI / PT / LWF / TDS / Gratuity / Bonus eligibility for many employees at once. Re-uploading the same employee_code updates the existing row."
          templateUrl="/statutory/employee-info/bulk-upload/template"
          uploadUrl="/statutory/employee-info/bulk-upload"
          uploadParams={{ company_id: selectedCompany }}
          templateFileName="employee_statutory_eligibility_template.csv"
          onUploadSuccess={() => fetchStatInfo()}
          supportsDryRun={true}
          columns={[
            'employee_code',
            'pf_eligible', 'uan', 'pf_number', 'voluntary_higher_pf', 'voluntary_pf_rate', 'pf_doj', 'is_international_worker', 'previous_employer_pf',
            'esi_eligible', 'esic_number', 'dispensary', 'ip_number', 'esi_entry_date', 'esi_exit_date',
            'pt_eligible', 'pt_state', 'lwf_eligible', 'lwf_state',
            'pan', 'tax_regime', 'is_senior_citizen', 'is_super_senior',
            'gratuity_eligible', 'bonus_eligible',
          ]}
        />
      </div>
    </MainLayout>
  );
};

export default EmployeeStatutoryEligibility;
